import type { PlayerId } from '../sim/ids';
import type { Simulation } from '../sim/Simulation';

/**
 * Tutorial steps (D117): each step is a hint plus a check on the real game
 * state, so the guide follows what the player actually did (in any order).
 */
export interface TutorialStep {
  readonly id: string;
  readonly text: { readonly touch: string; readonly mouse: string };
  done(sim: Simulation, me: PlayerId, ctx: { startUnits: number }): boolean;
}

const has = (sim: Simulation, me: PlayerId, type: string, complete = false) =>
  sim.buildings.ownedBy(me).some((b) => b.type === type && (!complete || b.state === 'complete'));

export const TUTORIAL_STEPS: readonly TutorialStep[] = [
  {
    id: 'camera',
    text: { touch: 'Drag the map with one finger to look around; pinch to zoom.', mouse: 'Drag the map (or WASD) to look around; wheel to zoom.' },
    done: () => false, // ticked by the scene the first time the camera moves
  },
  {
    id: 'farm',
    text: { touch: 'Food first: tap Farm in the bar at the bottom, tap a green spot near your base, then ✓.', mouse: 'Food first: pick Farm in the bar at the bottom (or press 3) and click a green spot near your base.' },
    done: (s, me) => has(s, me, 'farm'),
  },
  {
    id: 'sawmill',
    text: { touch: 'Wood next: build a Sawmill next to the trees.', mouse: 'Wood next: build a Sawmill next to the trees.' },
    done: (s, me) => has(s, me, 'sawmill'),
  },
  {
    id: 'housing',
    text: { touch: 'More people: build Survivor Housing so new survivors can join.', mouse: 'More people: build Survivor Housing so new survivors can join.' },
    done: (s, me) => has(s, me, 'survivor_housing'),
  },
  {
    id: 'barracks',
    text: { touch: 'Soldiers: build a Military Barracks.', mouse: 'Soldiers: build a Military Barracks.' },
    done: (s, me) => has(s, me, 'barracks', true),
  },
  {
    id: 'train',
    text: { touch: 'Tap the finished Barracks and train a Ranger.', mouse: 'Click the finished Barracks and train a Ranger.' },
    done: (s, me, c) => s.units.ownedBy(me).length > c.startUnits || s.units.ownedBy(me).length === 0,
  },
  {
    id: 'move',
    text: { touch: 'Tap Army to select your soldiers, then tap the ground to send them there.', mouse: 'Press Q (or the Army button) to select your soldiers, then click the ground to move them.' },
    done: (s, me) => s.units.ownedBy(me).some((u) => u.order.kind === 'move' || u.order.kind === 'attackMove'),
  },
  {
    id: 'tower',
    text: { touch: 'Defences: build a Watchtower between your base and the middle of the map.', mouse: 'Defences: build a Watchtower between your base and the middle of the map.' },
    done: (s, me) => has(s, me, 'watchtower'),
  },
  {
    id: 'horde',
    text: { touch: 'A small horde is coming. Keep your soldiers near the base and hold the line!', mouse: 'A small horde is coming. Keep your soldiers near the base and hold the line!' },
    done: (s) => !!s.match.result,
  },
];
